import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';

import moment from 'moment';
import IBANModule from 'iban';

import { Button, Field, Icon, Radios, SegmentedButton, Tag, Textarea } from '@ontruck/orc';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@ontruck/ontruck-react-components/lib/Modal';

import { Column, Row, Section, SectionTitle } from 'components/ModalFormLayout';

import CheckAndDateList from 'components/CheckAndDateList';
import { CARRIER_STATES, PREFERRED_LOCALES } from 'components/CarrierDetails/Constants';
import ReasonDeleteModal from 'components/ReasonDeleteModal';
import Select from 'react-select-v3';
import { reactSelectStyles } from 'utils/style';
import COUNTRIES from 'utils/countries.json';
import DynamicCarrierField from 'components/DynamicCarrierField';
import DateField from '../DateField';

class CarrierEditModal extends Component {
  static propTypes = {
    show: PropTypes.bool,
    carrier: PropTypes.object,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    remove: PropTypes.func,
    intl: PropTypes.any,
    className: PropTypes.string,
  };

  constructor(props) {
    super(props);

    const { carrier } = props;

    this.state = {
      carrier,
      fieldErrors: [],
      ibanPretty: IBANModule.printFormat(carrier.iban || '', ' '),
      ibanIsValid: IBANModule.isValid(carrier.iban || ''),
      server_errors: {},
      showDeleteModal: false,
      submitting: false,
      updatedCarrier: {
        id: carrier.id,
      },
      removeCarrier: {
        id: carrier.id,
      },
    };

    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
    this.showFieldError = this.showFieldError.bind(this);

    this.handleIbanChange = this.handleIbanChange.bind(this);
    this.renderIban = this.renderIban.bind(this);
    this.getCountryOption = this.getCountryOption.bind(this);

    this.FORMATED_COUNTRIES = COUNTRIES.map((country) => ({
      value: country['alpha-2'],
      label: country.name,
    }));
  }

  getCountryOption() {
    return (
      this.FORMATED_COUNTRIES.find(
        (country) => country.value === this.state.carrier.fiscal_country
      ) || null
    );
  }

  getPreferredLocaleOption() {
    return PREFERRED_LOCALES.find(
      (pl) => pl.value.substr(0, 2) === this.state.carrier?.preferred_locale.substr(0, 2)
    );
  }

  showFieldError(field) {
    const { fieldErrors } = this.state;

    return fieldErrors.indexOf(field) !== -1;
  }

  validate() {
    const { carrier, updatedCarrier } = this.state;

    const validDate = (value) => !value || moment(value).isValid();

    const validations = {
      name: (value) => value !== '',
      civil_liability_insurance_expiration: validDate,
      goods_transportation_insurance_expiration: validDate,
      social_security_certificate_expiration: validDate,
      tax_certificate_expiration: validDate,
      company_creation_date: validDate,
      iban: (value) => !value || IBANModule.isValid(value),
      ban_reason: (value) => {
        if (updatedCarrier.status === CARRIER_STATES.banned) {
          return value && value !== '';
        } else {
          return true;
        }
      },
    };

    return new Promise((resolve, reject) => {
      const fieldErrors = Object.keys(validations)
        .map((field) => {
          if (field in updatedCarrier) {
            return validations[field](carrier[field]) ? null : field;
          } else {
            return null;
          }
        })
        .filter((error) => error);

      if (fieldErrors.length === 0) {
        resolve();
      } else {
        this.setState(
          {
            fieldErrors,
          },
          () => reject(fieldErrors)
        );
      }
    });
  }

  handleIbanChange(iban, cb) {
    this.handleFieldChange('iban')(iban, () => {
      if (!IBANModule.isValid(iban)) {
        this.setState({
          fieldErrors: [...this.state.fieldErrors, 'iban'],
        });
      }
    });
  }

  renderIban(iban) {
    return IBANModule.isValid(iban) ? IBANModule.printFormat(iban, ' ') : iban;
  }

  handleSave() {
    const { onSave } = this.props;

    if (!onSave) {
      return;
    }

    const { updatedCarrier } = this.state;
    try {
      this.setState({ submitting: true }, async () => {
        await this.validate();
        await onSave(updatedCarrier);
        this.setState({ submitting: false });
      });
    } catch (err) {
      console.error(err);
    }
  }

  handleRemove() {
    const { remove } = this.props;

    if (!remove) {
      return;
    }

    const { removeCarrier } = this.state;

    remove(removeCarrier);
  }

  handleSubfieldChange(subfield, field) {
    return (value) => {
      const { carrier, updatedCarrier } = this.state;

      this.setState({
        carrier: {
          ...carrier,
          [subfield]: {
            ...carrier[subfield],
            [field]: value,
          },
        },
        updatedCarrier: {
          ...updatedCarrier,
          [subfield]: {
            ...updatedCarrier[subfield],
            [field]: value,
          },
        },
      });
    };
  }

  handleFieldChange(field, clearErrors = true) {
    return (
      value,
      cb = () => {
        //noop
      }
    ) => {
      const { carrier, fieldErrors, updatedCarrier } = this.state;

      this.setState(
        {
          carrier: {
            ...carrier,
            [field]: value,
          },
          fieldErrors: clearErrors ? fieldErrors.filter((error) => error !== field) : fieldErrors,
          updatedCarrier: {
            ...updatedCarrier,
            [field]: value,
          },
        },
        typeof cb === 'function'
          ? cb
          : () => {
              //noop
            }
      );
    };
  }

  render() {
    const { show, onClose, intl, className, isEditCarrierLite } = this.props;

    const { carrier, ibanIsValid, server_errors, submitting } = this.state;

    const t = intl.messages.components.CarrierEditModal;

    const dateList = [
      'civil_liability_insurance_expiration',
      'goods_transportation_insurance_expiration',
      'social_security_certificate_expiration',
      'tax_certificate_expiration',
      'company_creation_date',
    ];

    return (
      <div data-testid="carrierEditModal">
        <Modal show={show} size="L" onClose={onClose} className={className}>
          <div className="carrier-edit-modal">
            <ModalHeader>{t.titles.modal_edit}</ModalHeader>
            <ModalBody noPadding light>
              <Section className="carrier-edit-modal__section carrier-edit-modal__section--dark">
                <Row>
                  <Column>
                    <SectionTitle>{t.titles.carrier_status}</SectionTitle>
                    {(carrier.status === CARRIER_STATES.active ||
                      carrier.status === CARRIER_STATES.banned) && (
                      <Radios
                        girded
                        size="M"
                        name="status"
                        value={carrier.status}
                        items={[
                          { value: CARRIER_STATES.active, label: t.labels.active },
                          { value: CARRIER_STATES.banned, label: t.labels.banned },
                        ]}
                        onChange={(e, { name, value }) => {
                          this.handleFieldChange('status')(value, () => {
                            if (value === CARRIER_STATES.banned) {
                              this.handleFieldChange('ban_reason')('');
                            }
                          });
                        }}
                      />
                    )}
                    {carrier.status === CARRIER_STATES.not_active && (
                      <Tag
                        className="carrier-edit-modal__tag"
                        text={t.labels.not_activated}
                        skin="modernGrey"
                        weight="bold"
                        size="M"
                      />
                    )}
                    {carrier.status === CARRIER_STATES.banned && (
                      <Textarea
                        className="carrier-edit-modal__banned-field"
                        onChange={(e, { value }) => this.handleFieldChange('ban_reason')(value)}
                        name="ban_reason"
                        id="ban_reason"
                        label={t.labels.ban_reason}
                        value={carrier.ban_reason}
                        height={'55px'}
                        error={this.showFieldError('ban_reason') || null}
                      />
                    )}
                  </Column>
                </Row>
              </Section>
              <Section className="carrier-edit-modal__section">
                <SectionTitle className="carrier-edit-modal__title">
                  {t.titles.main_information}
                </SectionTitle>
                <Row>
                  <Column half>
                    <Field
                      id="name"
                      name="name"
                      className="carrier-edit-modal__field"
                      label={t.labels.name}
                      value={carrier.name || ''}
                      onChange={(e, { value }) => this.handleFieldChange('name')(value)}
                      error={(this.showFieldError('name') && t.errors.name) || null}
                    />
                  </Column>
                  {!isEditCarrierLite && (
                    <Column half>
                      <Field
                        id="google_drive_link"
                        name="google_drive_link"
                        className="carrier-edit-modal__field"
                        label={t.labels.documentation}
                        value={carrier.google_drive_link || ''}
                        onChange={(e, { value }) =>
                          this.handleFieldChange('google_drive_link')(value)
                        }
                      />
                    </Column>
                  )}
                </Row>
                <Row>
                  <Column>
                    <Radios
                      girded
                      size="M"
                      name="type"
                      value={'is_autonomous' in carrier ? carrier.is_autonomous.toString() : 'true'}
                      items={[
                        { value: 'false', label: t.labels.fleet },
                        { value: 'true', label: t.labels.self_employee },
                      ]}
                      onChange={(e, { name, value }) => {
                        this.handleFieldChange('is_autonomous')(value === 'true');
                      }}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column third>
                    <SectionTitle className="carrier-edit-modal__title">
                      {t.titles.company_creation_date}
                    </SectionTitle>
                    <div className="creation-date-edit" data-testid="company_creation_date_field">
                      <DateField
                        labelClass="carrier-edit-modal__date-label"
                        value={carrier.company_creation_date}
                        onChange={(value) => {
                          const formatValue = moment(value).format('YYYY-MM-DD');
                          this.handleFieldChange('company_creation_date')(formatValue);
                        }}
                        size="M"
                        error={
                          (this.showFieldError('company_creation_date') &&
                            t.errors.company_creation_date) ||
                          null
                        }
                      />
                    </div>
                  </Column>
                </Row>
              </Section>

              <Section className="carrier-edit-modal__section">
                <SectionTitle className="carrier-edit-modal__title">
                  {t.titles.payment}
                </SectionTitle>
                <Row>
                  <Column>
                    <SegmentedButton.Parent
                      onChange={(e, { name, value }) => {
                        this.handleFieldChange('fiscal_position')(value);
                      }}
                      value={carrier.fiscal_position}
                    >
                      <SegmentedButton.Child
                        id="fiscal-position-direct"
                        name="fiscal-position-direct"
                        value="DIRECT"
                        label={t.labels.fiscal_position_direct}
                        checked={carrier.fiscal_position === 'DIRECT'}
                      />
                      <SegmentedButton.Child
                        id="fiscal_position_irpf_1"
                        name="fiscal_position_irpf_1"
                        value="IRPF_1"
                        label={t.labels.fiscal_position_irpf_1}
                        checked={carrier.fiscal_position === 'IRPF_1'}
                      />
                    </SegmentedButton.Parent>
                  </Column>
                </Row>
                <Row>
                  <Column third>
                    <Field
                      id="tax_number"
                      name="tax_number"
                      className="carrier-edit-modal__field"
                      label={t.labels.tax_number}
                      value={carrier.tax_number || ''}
                      onChange={(e, { value }) => this.handleFieldChange('tax_number')(value)}
                      error={(this.showFieldError('tax_number') && t.errors.tax_number) || null}
                    />
                  </Column>
                  <Column third>
                    <Field
                      id="iban"
                      name="iban"
                      label={t.labels.iban}
                      value={this.renderIban(carrier.iban) || ''}
                      onChange={(e, { value }) => this.handleIbanChange(value)}
                      className="carrier-edit-modal__field carrier-edit-modal__ibanfield"
                      maxLength="50"
                      error={(this.showFieldError('iban') && t.errors.iban) || null}
                    >
                      {ibanIsValid ? (
                        <Icon glyph="check" className="carrier-edit-modal__ibanCheck" />
                      ) : null}
                    </Field>
                  </Column>
                  <Column third>
                    <Field
                      id="billing_email"
                      name="billing_email"
                      label={t.labels.billing_email}
                      value={carrier.billing_email || ''}
                      onChange={(e, { value }) => this.handleFieldChange('billing_email')(value)}
                      className="carrier-edit-modal__field"
                      maxLength="254"
                    />
                  </Column>
                </Row>
              </Section>

              <Section className="carrier-edit-modal__section">
                <SectionTitle className="carrier-edit-modal__title">
                  {t.titles.fiscal_address}
                </SectionTitle>
                <Row>
                  <Column>
                    <Field
                      id="address_line"
                      name="address_line"
                      className="carrier-edit-modal__field"
                      label={t.labels.billing_address_address_line}
                      value={
                        (carrier.billing_address && carrier.billing_address.address_line) || ''
                      }
                      onChange={(e, { value }) =>
                        this.handleSubfieldChange('billing_address', 'address_line')(value)
                      }
                    />
                  </Column>
                </Row>
                <Row>
                  <Column third>
                    <Field
                      id="zipcode"
                      name="zipcode"
                      className="carrier-edit-modal__field"
                      label={t.labels.billing_address_zipcode}
                      value={(carrier.billing_address && carrier.billing_address.zipcode) || ''}
                      onChange={(e, { value }) =>
                        this.handleSubfieldChange('billing_address', 'zipcode')(value)
                      }
                    />
                  </Column>
                  <Column third>
                    <Field
                      id="address_city"
                      name="address_city"
                      className="carrier-edit-modal__field"
                      label={t.labels.billing_address_city}
                      value={(carrier.billing_address && carrier.billing_address.city) || ''}
                      onChange={(e, { value }) =>
                        this.handleSubfieldChange('billing_address', 'city')(value)
                      }
                    />
                  </Column>
                  {!isEditCarrierLite && (
                    <Column third>
                      <Select
                        containerWidth="auto"
                        placeholder={t.labels.billing_country}
                        name="country"
                        value={this.getCountryOption()}
                        options={this.FORMATED_COUNTRIES}
                        onChange={(selectedOption) =>
                          this.handleFieldChange('fiscal_country')(selectedOption.value)
                        }
                        styles={reactSelectStyles}
                      />
                    </Column>
                  )}
                </Row>
              </Section>

              <Section className="carrier-edit-modal__section">
                <SectionTitle className="carrier-edit-modal__title">
                  {t.titles.contact}
                </SectionTitle>
                <Row>
                  <Column third>
                    <Field
                      id="contact_name"
                      name="contact_name"
                      className="carrier-edit-modal__field"
                      label={t.labels.contact_name}
                      value={carrier.contact_name || ''}
                      onChange={(e, { value }) => this.handleFieldChange('contact_name')(value)}
                    />
                  </Column>
                  <Column third>
                    <Field
                      id="contact_phone"
                      name="contact_phone"
                      className="carrier-edit-modal__field"
                      label={t.labels.contact_phone}
                      value={carrier.phone || ''}
                      onChange={(e, { value }) => this.handleFieldChange('phone')(value)}
                    />
                  </Column>
                  <Column third>
                    <Field
                      id="phone_secondary"
                      name="phone_secondary"
                      className="carrier-edit-modal__field"
                      label={t.labels.phone_secondary}
                      value={carrier.phone_secondary || ''}
                      onChange={(e, { value }) => this.handleFieldChange('phone_secondary')(value)}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column third>
                    <Field
                      id="email"
                      name="email"
                      className="carrier-edit-modal__field"
                      label={t.labels.coms_email}
                      value={carrier.email || ''}
                      onChange={(e, { value }) => this.handleFieldChange('email')(value)}
                      maxLength="254"
                    />
                  </Column>
                  {!isEditCarrierLite && (
                    <Column third>
                      <Field
                        id="pod_email"
                        name="pod_email"
                        label={t.labels.pod_email}
                        value={carrier.pod_email || ''}
                        onChange={(e, { value }) => this.handleFieldChange('pod_email')(value)}
                        className="carrier-edit-modal__field"
                        maxLength="254"
                      />
                    </Column>
                  )}
                  <Column third />
                </Row>
              </Section>

              {!isEditCarrierLite && (
                <Section className="carrier-edit-modal__section">
                  <SectionTitle className="carrier-edit-modal__title">
                    {t.titles.language}
                  </SectionTitle>
                  <Row>
                    <Column third>
                      <Select
                        containerWidth="auto"
                        placeholder={t.labels.preferred_locale}
                        name="preferred_locale"
                        value={this.getPreferredLocaleOption()}
                        options={PREFERRED_LOCALES}
                        onChange={(selectedOption) =>
                          this.handleFieldChange('preferred_locale')(selectedOption.value)
                        }
                        styles={reactSelectStyles}
                      />
                    </Column>
                    <Column third />
                    <Column third />
                  </Row>
                </Section>
              )}
              {!isEditCarrierLite &&
                carrier.country_specific_extra_fields &&
                carrier.country_specific_extra_fields.length > 0 && (
                  <Section className="carrier-edit-modal__section">
                    <SectionTitle className="carrier-edit-modal__title">
                      {t.titles.country_fields}
                    </SectionTitle>
                    <Row>
                      {carrier.country_specific_extra_fields.map((field) => (
                        <Column third>
                          <DynamicCarrierField
                            field={field}
                            handleFieldChange={this.handleFieldChange}
                            carrier={carrier}
                          />
                        </Column>
                      ))}
                    </Row>
                  </Section>
                )}
              {!isEditCarrierLite && (
                <Section className="carrier-edit-modal__section">
                  <SectionTitle className="carrier-edit-modal__title">{t.titles.docs}</SectionTitle>
                  <Row>
                    <Column half></Column>
                  </Row>
                  <Row>
                    <Column half>
                      <CheckAndDateList
                        fieldList={dateList}
                        object={carrier}
                        t={t.labels}
                        size="M"
                        handleFieldChange={this.handleFieldChange}
                        checkboxClassname="carrier-edit-modal__doc-checkbox"
                        inputClass="carrier-edit-modal__input carrier-edit-modal__input--inline"
                        errors={Object.assign(
                          ...dateList.map((name) =>
                            this.showFieldError(name) ? { [name]: t.errors.date } : {}
                          )
                        )}
                      />
                    </Column>
                  </Row>
                </Section>
              )}
            </ModalBody>

            <ModalFooter light>
              {Object.keys(server_errors).length ? (
                <div className={'carrier-edit-modal__general-errors'}>
                  <div>
                    {Object.keys(server_errors).map((error) => (
                      <div>
                        <strong>{error}</strong>: {server_errors[error]}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="carrier-edit-modal__footer-actions">
                {carrier.id && (
                  <span className="carrier-edit-modal__delete-btn">
                    <Button
                      nude
                      text={t.delete}
                      onClick={() => this.setState({ showDeleteModal: true })}
                      data-testid="carrier-edit-modal__button-modal"
                    />
                  </span>
                )}
                <Button
                  data-testid="editCarrier__submit"
                  onClick={this.handleSave}
                  disabled={submitting}
                  text={t.edit_save}
                />
              </div>
            </ModalFooter>
          </div>
        </Modal>
        <ReasonDeleteModal
          show={this.state.showDeleteModal}
          onClose={() => this.setState({ showDeleteModal: false })}
          disabled={submitting}
          onSave={(reason) => {
            this.setState(
              {
                removeCarrier: {
                  ...this.state.removeCarrier,
                  remove_reason: reason,
                  status: CARRIER_STATES.removed,
                },
              },
              this.handleRemove
            );
          }}
        />
      </div>
    );
  }
}

export default injectIntl(CarrierEditModal);
